/**
 * Suggestion chips derived from the CONNECTED sellers. The seller AUTHORS each
 * skill's suggestion (label + prompt) in its discovery doc; the UI renders it
 * verbatim — no phrasing is hardcoded here. We only handle layout: round-robin one
 * chip per skill across sellers so the row isn't all one seller.
 *
 * The agent emits its own per-turn `suggestions` once a conversation is going; these
 * derived chips are the STARTER set (and the fallback when the agent emits none).
 */

/**
 * Chip accent palette. Flows are dynamic (`sellerId/skillId` from discovery, or a
 * seller's own flow id) — NOT a fixed enum — so we don't hardcode a per-flow map.
 * Instead each flow deterministically hashes to one of a curated set of Cloudscape-
 * friendly accents: same flow → same color every render, new sellers get a color for
 * free, and nothing needs updating when the seller registry changes.
 */
const PALETTE = [
  { bd: '#0972d3', fg: '#0972d3', bg: '#f0f7ff' }, // blue
  { bd: '#8d6a00', fg: '#8d6a00', bg: '#fef6e7' }, // amber
  { bd: '#6f42c1', fg: '#6f42c1', bg: '#f5f0ff' }, // purple
  { bd: '#037f0c', fg: '#037f0c', bg: '#effff1' }, // green
  { bd: '#3f51b5', fg: '#3f51b5', bg: '#eef0fb' }, // indigo
  { bd: '#d91515', fg: '#d91515', bg: '#fff0f0' }, // red
  { bd: '#0d7d7d', fg: '#0d7d7d', bg: '#eefafa' }, // teal
  { bd: '#7c2128', fg: '#7c2128', bg: '#fff0f0' }, // claret
  { bd: '#b7791f', fg: '#b7791f', bg: '#fdf6e3' }, // gold
];
const NEUTRAL = { bd: '#879596', fg: '#414d5c', bg: '#f4f4f4' };

export function colorFor(flow) {
  const f = String(flow || '');
  if (!f) return NEUTRAL;
  // Stable string hash → palette index (FNV-1a).
  let h = 2166136261;
  for (let i = 0; i < f.length; i++) {
    h ^= f.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return PALETTE[(h >>> 0) % PALETTE.length];
}

/** Seeded Fisher-Yates shuffle (LCG) — stable for a given seed, varies across seeds.
 *  Lets starter chips RANDOMIZE across sessions while staying put across re-renders. */
function seededShuffle(arr, seed) {
  const a = arr.slice();
  let s = (seed >>> 0) || 1;
  const rnd = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
  };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Build starter chips from discovered sellers using each skill's seller-authored
 * suggestion. The set is RANDOMIZED per `seed` (seller order + each seller's skill
 * order are shuffled), so the prompts shown vary by session and reflect a different
 * mix of the connected sellers each time — never a fixed, hardcoded list. Round-robins
 * one chip per skill across sellers (so the row isn't all one seller), capped at `max`.
 *
 * @param {object} discovery   the discovered-sellers snapshot
 * @param {number} max         max chips to return
 * @param {object} [opts]
 * @param {string[]} [opts.disabledSellers]  seller ids turned OFF for the agent (excluded)
 * @param {number}   [opts.seed]             randomization seed (re-roll for a new mix)
 */
export function deriveStarterChips(discovery, max = 5, opts = {}) {
  const { disabledSellers = [], seed = 1 } = opts;
  let sellers = (discovery && discovery.sellers) || [];
  if (!sellers.length) return [];

  // Only suggest things the agent can actually do: drop disabled sellers.
  const disabled = new Set(disabledSellers);
  sellers = sellers.filter((s) => !disabled.has(s.sellerId));
  if (!sellers.length) return [];

  const shuffledSellers = seededShuffle(sellers, seed);
  const queues = shuffledSellers.map((s, si) =>
    seededShuffle(s.skills || [], seed + si * 101 + 7)
      .map((sk) => {
        const sug = sk.suggestion;
        if (!sug || !sug.label || !sug.prompt) return null; // only render what the seller authored
        return { flow: sug.flow || sk.skillId, label: sug.label, prompt: sug.prompt };
      })
      .filter(Boolean),
  );

  const out = [];
  const seen = new Set();
  let added = true;
  while (added && out.length < max) {
    added = false;
    for (const q of queues) {
      const c = q.shift();
      if (!c) continue;
      added = true;
      if (seen.has(c.label)) continue;
      seen.add(c.label);
      out.push(c);
      if (out.length >= max) break;
    }
  }
  return out;
}
